import { useMemo, useCallback, useState } from "react";
import { Navigate } from "react-router-dom";
import { useMe } from "../../hooks/useMe.js";
import { apiJson } from "../../utils/api.js";
import { getTelegramMiniAppUser } from "../../utils/telegramUser.js";
import { PageReveal } from "../components/PageReveal.jsx";

function roleLabel(role) {
  if (role === "rop") return "Руководитель отдела";
  if (role === "moderator") return "Модератор";
  if (role === "manager") return "Менеджер";
  if (role === "admin") return "Администратор";
  return role || "—";
}

function buildDisplayName(me, tg) {
  const dn = (me?.display_name || "").trim();
  if (dn) return dn;
  const a = (tg?.first_name || "").trim();
  const b = (tg?.last_name || "").trim();
  const combined = [a, b].filter(Boolean).join(" ");
  if (combined) return combined;
  if (tg?.username) return `@${tg.username}`;
  return "Имя не указано";
}

function initials(name) {
  const p = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!p.length) return "?";
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[1][0]).toUpperCase();
}

export function ProfilePage() {
  const { me, loading, isManagerStaff, refetch } = useMe();
  const [dayBusy, setDayBusy] = useState(false);

  const tg = useMemo(() => getTelegramMiniAppUser(), []);
  const displayName = useMemo(() => buildDisplayName(me, tg), [me, tg]);

  const toggleWorkDay = useCallback(async () => {
    const active = me?.profile?.work_day_active;
    setDayBusy(true);
    try {
      await apiJson(active ? "/api/me/work-day/end" : "/api/me/work-day/start", { method: "POST" });
      await refetch();
    } catch (e) {
      window.alert(e?.message || "Ошибка");
    } finally {
      setDayBusy(false);
    }
  }, [me?.profile?.work_day_active, refetch]);

  if (!loading && !isManagerStaff) {
    return <Navigate to="/deals" replace />;
  }

  const workDayActive = !!me?.profile?.work_day_active;
  const startedAt = me?.profile?.work_day_started_at;
  const departments = Array.isArray(me?.department_titles) ? me.department_titles : [];
  const code = (me?.code || "").trim();
  const linkToken = (me?.telegram_username || tg?.username || "").trim();
  const tgId = me?.telegram_id != null ? String(me.telegram_id) : "";

  return (
    <PageReveal ready={!loading && !!me}>
      <main className="chat profilePageMain">
        <div className="screenScroll profilePage profilePageScroll">
          <div className="profileIdentity">
            <div className="profileAvatarWrap">
              {tg?.photo_url ? (
                <img className="profileAvatarImg" src={tg.photo_url} alt="" />
              ) : (
                <div className="profileAvatarPlaceholder">{initials(displayName)}</div>
              )}
            </div>
            <div className="profileIdentityText">
              <div className="profileDisplayName">{displayName}</div>
              <div className="profileRolePill">{roleLabel(me?.role)}</div>
            </div>
          </div>

          {(code || linkToken || tgId || tg?.username) ? (
            <dl className="profileMetaList">
              {code ? (
                <>
                  <dt className="profileMetaDt">Код</dt>
                  <dd className="profileMetaDd">{code}</dd>
                </>
              ) : null}
              {linkToken ? (
                <>
                  <dt className="profileMetaDt">Ссылка для клиентов</dt>
                  <dd className="profileMetaDd">
                    <span className="profileMetaMono">t.me/AutoDeskContact_bot?start={linkToken}</span>
                  </dd>
                </>
              ) : null}
              {tgId ? (
                <>
                  <dt className="profileMetaDt">Telegram ID</dt>
                  <dd className="profileMetaDd profileMetaMono">{tgId}</dd>
                </>
              ) : null}
              {tg?.username ? (
                <>
                  <dt className="profileMetaDt">Username</dt>
                  <dd className="profileMetaDd">@{tg.username}</dd>
                </>
              ) : null}
            </dl>
          ) : null}

          {departments.length > 0 ? (
            <section className="profileSection profileSectionFlush">
              <h2 className="profileSectionTitle">Направления</h2>
              <ul className="profileDeptList">
                {departments.map((t) => (
                  <li key={t} className="profileDeptItem">
                    {t}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="profileSection profileSectionFlush">
            <h2 className="profileSectionTitle">Рабочий день</h2>
            {workDayActive && startedAt ? (
              <p className="profileWorkDayStatus">
                Смена начата:{" "}
                {new Date(startedAt).toLocaleString("ru-RU", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            ) : workDayActive ? (
              <p className="profileHint">Смена активна</p>
            ) : (
              <p className="profileHint">Смена не начата</p>
            )}
          </section>
        </div>

        <div className="profileWorkDayDock">
          <button
            type="button"
            className={`profileDayBtn ${workDayActive ? "profileDayBtnEnd" : "profileDayBtnStart"}`}
            disabled={dayBusy}
            onClick={toggleWorkDay}
          >
            {workDayActive ? "Закончить рабочий день" : "Начать рабочий день"}
          </button>
        </div>
      </main>
    </PageReveal>
  );
}
